import React from "react";
import { formatSquadInfoShort } from "../../UI/DesignStandards";
import TeamScores from "./TeamScoreCard/TeamScores";
import TeamActions from "./TeamScoreCard/TeamActions";
import { laneAssignmentLabel } from "../../../utility/laneAssignment";
import {
  teamBowlersAveragesVerified,
  teamHasAllRequiredScores,
} from "../../../utility/recapSheets";
import { useTournamentSlug } from "../../../context/TournamentRouteContext";

const TeamScoreCard = ({
  team,
  onScoreUpdate,
  onTeamDelete,
  onVerificationChange,
  calculatingTotals,
  getCalculationResult,
  canManage = false,
  canEnterScores = false,
  tournamentId,
  canVerifyTeamForEdit,
  verificationStatus,
  isScoreSaving,
  onReview,
  onRecapDelete,
  importedTeamIds = [],
}) => {
  const tournamentSlug = useTournamentSlug();

  // Get team totals from cache instead of team state
  const teamScratchTotal =
    getCalculationResult(team.id, "team", "scratch")?.teamScratchTotal || 0;
  const teamHandicapTotal =
    getCalculationResult(team.id, "team", "handicap")?.teamHandicapTotal || 0;

  const bowlers = team.bowlers || [];
  const laneLabel = laneAssignmentLabel(team);

  // Check if this team is currently calculating totals
  const isCalculatingTotals =
    calculatingTotals && typeof calculatingTotals.has === "function"
      ? calculatingTotals.has(team.id)
      : false;

  const allBowlersVerified = teamBowlersAveragesVerified(team);

  // Get team verification status from the new verification system
  const teamVerificationStatus = verificationStatus?.scores?.find(
    (vs) => vs.entityId === team.id
  );
  // Prioritize local team state for optimistic updates, fall back to verificationStatus
  const isTeamVerified =
    team.isVerified !== undefined
      ? team.isVerified
      : teamVerificationStatus?.isVerified || false;

  // Check if team has any scores entered
  const teamHasScores =
    team.transformedScores &&
    Object.values(team.transformedScores).some((gameScores) =>
      Object.values(gameScores).some((score) => score !== null && score !== "")
    );

  const teamHasAllScores = teamHasAllRequiredScores(team);

  const squadLabel = team.squad?.squadDate
    ? formatSquadInfoShort(team.squad.squadDate, team.squad.squadTime)
    : null;

  // Squad at top-right only when there's no team name (otherwise it's in the name row)
  const squadLabelAtTop = squadLabel && !team.teamName;
  // Extra top margin only when squad is at top (no team name)
  const contentTopMargin = squadLabelAtTop ? "mt-3" : "mt-0";

  return (
    <div className="card-board overflow-hidden relative">
      {/* Squad label - top-right only when no team name */}
      {squadLabelAtTop && (
        <div
          className="absolute top-3 right-3 pb-2 text-xs text-brand-muted whitespace-nowrap"
          aria-label={`Squad: ${squadLabel}`}
        >
          {squadLabel}
          {laneLabel ? ` · ${laneLabel}` : ""}
        </div>
      )}
      {/* Content wrapper: extra top margin only when squad is at top (no team name) */}
      <div className={contentTopMargin}>
        {/* Team name display - only show if team has a name */}
        {team.teamName && (
          <div className="px-4 pt-3 pb-1">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between md:gap-3">
              <div
                className="hidden md:block flex-1 min-w-0"
                aria-hidden="true"
              />
              <div className="flex items-center justify-center gap-3 flex-shrink-0">
                <h2 className="text-lg font-display font-bold text-brand-charcoal text-center">
                  {team.teamName}
                </h2>
                {laneLabel ? (
                  <span className="text-xs text-brand-muted whitespace-nowrap">
                    {laneLabel}
                  </span>
                ) : null}
                {isTeamVerified && (
                  <span className="border border-brand-charcoal/20 text-brand-charcoal px-2.5 py-1 rounded-lg text-xs font-display font-bold tracking-wide">
                    VERIFIED
                  </span>
                )}
              </div>
              {squadLabel && (
                <div className="text-center md:flex-1 md:flex md:justify-end min-w-0">
                  <span
                    className="text-xs text-brand-muted whitespace-nowrap"
                    aria-label={`Squad: ${squadLabel}`}
                  >
                    {squadLabel}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Team Scores */}
        <TeamScores
          team={team}
          bowlers={bowlers}
          onScoreUpdate={onScoreUpdate}
          canEnterScores={canEnterScores}
          isCalculatingTotals={isCalculatingTotals}
          getCalculationResult={getCalculationResult}
          teamScratchTotal={teamScratchTotal}
          teamHandicapTotal={teamHandicapTotal}
          isTeamVerified={isTeamVerified}
          verificationStatus={verificationStatus}
          isScoreSaving={isScoreSaving}
        />

        {/* Team Actions */}
        <TeamActions
          team={team}
          tournamentId={tournamentId}
          tournamentSlug={tournamentSlug}
          isTeamVerified={isTeamVerified}
          canManage={canManage}
          canEnterScores={canEnterScores}
          canVerifyTeamForEdit={canVerifyTeamForEdit}
          onVerificationChange={onVerificationChange}
          onReview={onReview}
          onTeamDelete={onTeamDelete}
          onRecapDelete={onRecapDelete}
          verificationStatus={verificationStatus}
          allBowlersVerified={allBowlersVerified}
          teamHasScores={teamHasScores}
          teamHasAllRequiredScores={teamHasAllScores}
          importedTeamIds={importedTeamIds}
        />
      </div>
    </div>
  );
};

export default TeamScoreCard;
